"use client";

/**
 * Live open-review counts for the signed-in admin.
 *
 * GET /v1/public/global/admin/pending-work
 *
 * `tiles` keys match admin home route ids (`blogs`, `hub`). `total` is the
 * sum of those tiles and drives the nav indicator.
 */

import { useEffect, useState } from "react";
import { apiRequestExternal } from "./apiRequestExternal";
import { apiValidateArguments } from "./apiValidateArguments";
import { composerBrowserBaseUrl } from "./controllerRegistryAdmin";

export type AdminPendingWork = {
  tiles: Record<string, number>;
  details: Record<string, Record<string, number>>;
  total: number;
};

const emptyWork = (): AdminPendingWork => ({ tiles: {}, details: {}, total: 0 });

export function tilePendingCount(
  work: AdminPendingWork | null | undefined,
  tileId: string,
): number {
  const count = work?.tiles?.[tileId];
  return typeof count === "number" && count > 0 ? count : 0;
}

export async function fetchAdminPendingWork(accessToken: string): Promise<AdminPendingWork> {
  apiValidateArguments({ accessToken });
  const url = `${composerBrowserBaseUrl().replace(/\/$/, "")}/v1/public/global/admin/pending-work`;
  const result = await (apiRequestExternal as Function)(
    url,
    "GET",
    null,
    { Authorization: accessToken },
    false,
    false,
  );
  const body = result?.tiles ? result : result?.data;
  if (!body || typeof body !== "object") {
    return emptyWork();
  }
  return {
    tiles: body.tiles && typeof body.tiles === "object" ? body.tiles : {},
    details: body.details && typeof body.details === "object" ? body.details : {},
    total: typeof body.total === "number" ? body.total : 0,
  };
}

/**
 * Load pending-work counts once per token. A failed request leaves counts empty
 * so the indicators stay hidden.
 */
export function useAdminPendingWork(
  accessToken: string | null | undefined,
  enabled: boolean,
): AdminPendingWork | null {
  const [work, setWork] = useState<AdminPendingWork | null>(null);

  useEffect(() => {
    if (!enabled || !accessToken) {
      setWork(null);
      return undefined;
    }
    let cancelled = false;
    fetchAdminPendingWork(accessToken)
      .then((result) => {
        if (!cancelled) {
          setWork(result);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setWork(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken, enabled]);

  return work;
}
